import React, { useState, useEffect } from 'react';
import { Institution, InstitutionBranding } from '../../types/institution';
import { AccountResponse, accountService, institutionService } from '../../services/api';
import { track, EVENTS } from '../../analytics';
import NoAccountsFound from './NoAccountsFound';
import { useToast } from '../../contexts/ToastContext';
import selectedCheckmarkIcon from '../../assets/icons/account-selected-checkmark.svg';
import uncheckedCheckboxIcon from '../../assets/icons/unchecked-checkbox.svg';
import './AccountSelectionStep.css';

export interface AccountSelectionStepProps {
  institution: Institution;
  onNext: () => void;
  onPrev: () => void;
}

const AccountSelectionStep: React.FC<AccountSelectionStepProps> = ({
  institution,
  onNext,
  onPrev
}) => {
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<AccountResponse[]>([]);
  const [branding, setBranding] = useState<InstitutionBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const { showToast } = useToast();

  // Track controlBackToDayli event when user lands on this component
  useEffect(() => {
    const trackEvent = async () => {
      await track(EVENTS.controlBackToDayli, {
        userRedirected: 'yes'
      });
    };
    trackEvent();
  }, []);

  // Fetch institution branding and accounts on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch branding and accounts in parallel
        const [brandingData, accountsData] = await Promise.all([
          institutionService.getInstitutionBranding(institution.id),
          accountService.getAccountsByInstitution(institution.id)
        ]);

        setBranding(brandingData);
        
        // Filter accounts to only show checking accounts
        const checkingAccounts = accountsData.filter(account => account.type === 'checking');
        
        // Sort accounts by balance in descending order (highest first)
        const sortedAccounts = [...checkingAccounts].sort((a, b) => b.balance - a.balance);
        setAccounts(sortedAccounts);
        
        // Select all accounts by default
        setSelectedAccounts(sortedAccounts.map(account => account.id));
        
        // Track account selection page view with account IDs
        // Convert array to comma-separated string for GA4 compliance
        const accountIdsArray = sortedAccounts.map(account => account.id);
        await track(EVENTS.accountSelectionPageView, {
          accountIdsList: accountIdsArray.join(',')
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load account data';
        setError(errorMessage);
        showToast({
          title: 'Something went wrong',
          message: errorMessage
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [institution.id]);

  const handleAccountToggle = (accountId: string) => {
    // Find the account object to get account number
    const account = accounts.find(acc => acc.id === accountId);
    // Use raw account number as received from API (no formatting)
    const accountNumberMasked = account ? account.accountNumberDisplay : '';
    
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        // Account is being unchecked
        track(EVENTS.accountsUncheckedAction, {
          accountId: accountId,
          accountNumberMasked: accountNumberMasked
        });
        return prev.filter(id => id !== accountId);
      } else {
        // Account is being checked
        track(EVENTS.accountsCheckedAction, {
          accountId: accountId,
          accountNumberMasked: accountNumberMasked
        });
        return [...prev, accountId];
      }
    });
  };

  const handleSave = async () => {
    if (selectedAccounts.length === 0) {
      const errorMessage = 'Please select at least one account';
      setError(errorMessage);
      showToast({
        title: 'Something went wrong',
        message: errorMessage,
        autoClose: false
      });
      return;
    }

    // Track save CTA clicked event
    // Convert array to comma-separated string for GA4 compliance
    await track(EVENTS.saveCtaClicked, {
      ctaType: 'continue',
      action: 'saveAccount',
      accountIds: selectedAccounts.join(',')
    });

    try {
      setSaving(true);
      setError('');

      // Persist selected accounts with account_ids array
      await accountService.persistSelectedAccounts(selectedAccounts);

      // Move to next step (verification/waitlist screen)
      onNext();
    } catch (err) {
      console.error('Error persisting accounts:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save accounts';
      setError(errorMessage);
      showToast({
        title: 'Something went wrong',
        message: errorMessage,
        autoClose: false
      });
    } finally {
      setSaving(false);
    }
  };

  const formatAccountNumber = (accountNumber: string) => {
    // Format account number to show last 4 digits with bullet points
    if (accountNumber.includes('•')) return accountNumber;
    return `•••• ${accountNumber.slice(-4)}`;
  };

  const formatBalance = (balance: number) => {
    return `Balance: $${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getAccountName = (name: string) => {
    return `${name} Account`;
  };

  // Get bank icon from branding or fallback
  const getBankIcon = () => {
    return branding?.icon ||
      branding?.logo ||
      institution.branding?.icon ||
      institution.branding?.logo ||
      '/assets/default-bank.svg';
  };

  if (loading) {
    return (
      <div className="account-selection-step">
        <div className="account-selection-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <span>Fetching your bank accounts</span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error && accounts.length === 0) {
    // Track error page view
    track(EVENTS.errorPageView, {
      errorMessage: error
    });
    
    return (
      <div className="account-selection-step">
        <div className="account-selection-container">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ marginBottom: '20px', color: '#163224' }}>
              We're experiencing some technical difficulties. Please try again later.
            </p>
            <button onClick={onPrev} className="save-button">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show no accounts found component when no accounts are returned (successful load but no accounts)
  if (!loading && accounts.length === 0 && !error) {
    // Track error page view for no accounts found
    track(EVENTS.errorPageView, {
      errorMessage: 'No accounts found'
    });
    
    return (
      <div className="account-selection-step">
        <div className="account-selection-container">
          <NoAccountsFound onBack={onPrev} />
        </div>
      </div>
    );
  }

  return (
    <div className="account-selection-step">
      <div className="account-selection-container">
        {/* Header */}
        <div className="account-selection-header">
          <h1 className="main-heading">Link a checking account (at least $500 in monthly debit card spends) to get Dayli card . </h1>
        </div>

        {/* Accounts List - Scrollable */}
        <div className="accounts-list-wrapper">
          <div className="accounts-list">
            {accounts.map((account) => (
              <div
                key={account.id}
                className={`account-card ${selectedAccounts.includes(account.id) ? 'selected' : ''}`}
                onClick={() => handleAccountToggle(account.id)}
              >
                <div className="account-card-content">
                  <div className="account-info">
                    <div className="bank-icon">
                      <img
                        src={getBankIcon()}
                        alt={institution.name}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/assets/default-bank.svg';
                        }}
                      />
                    </div>
                    <div className="account-details">
                      <div className="account-name">{getAccountName(account.name)}</div>
                      <div className="account-number">{formatAccountNumber(account.accountNumberDisplay)}</div>
                      <div className="account-balance">{formatBalance(account.balance)}</div>
                    </div>
                  </div>
                  <div className="checkbox-container">
                    {selectedAccounts.includes(account.id) ? ( 
                      <div className="checkbox-selected">
                        <img
                          src={selectedCheckmarkIcon}
                          alt="Selected"
                        />
                      </div>
                    ) : (
                      <div className="checkbox-unselected">
                        <img
                          src={uncheckedCheckboxIcon}
                          alt="Unselected"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Save Button */}
        <div className="save-button-container">
          <button
            className="save-button"
            onClick={handleSave}
            disabled={selectedAccounts.length === 0 || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSelectionStep; 